<template>
  <div class="packaging-step">
    <div
      v-if="shouldShowPackagingSection"
      class="alert alert-info flex-column flex-lg-row mb-6 gap-3 px-2 pt-2 pb-3 px-lg-4 pt-lg-4 pb-lg-5"
    >
      <!-- Products available to use as packaging -->
      <template v-if="packagingCandidatesWithCurrentPackage.length >= 1">
        <div class="d-flex flex-column flex-grow-1">
          <span class="h6 mb-2">
            Product<template
              v-if="packagingCandidatesWithCurrentPackage.length > 1"
              >en</template
            >
            gevonden die ook als verpakking kan dienen:</span
          >
          <div class="row gx-2 gy-1 packaging-step__cards">
            <div
              v-for="item in packagingCandidatesWithCurrentPackage"
              :key="item.id"
            >
              <div class="packaging-step__card col-12">
                <figure
                  v-if="item.imageUrl || item.image"
                  class="image col-auto"
                >
                  <img
                    :src="item?.images?.medium || item.imageUrl || item.image"
                    :alt="decodedItemName(item.name)"
                  />
                </figure>
                <span
                  class="name fs-sm fw-medium text-black lh-sm col col-md flex-grow-1"
                  >{{ decodedItemName(item.name) }}</span
                >
                <div class="col-12 col-md-auto">
                  <button
                    v-if="selectedPackagingProduct === item"
                    class="btn btn--secondary btn--sm"
                    @click="handleUpdate(item)"
                    aria-label="Gekozen als verpakking"
                  >
                    Gekozen als verpakking
                  </button>
                  <button
                    v-else
                    class="btn btn--gray btn--sm"
                    @click="handleUpdate(item)"
                    aria-label="Gebruik dit item"
                  >
                    Kies als verpakking
                  </button>
                </div>
              </div>
            </div>
          </div>

          <span class="text-gray-700 text fs-xs mt-05">
            Als je dit kiest hoef je hieronder geen aparte verpakking meer te
            selecteren.
          </span>
        </div>
      </template>
    </div>

    <div>
      <ProductSelector
        ref="productSelector"
        endpoint="boxes"
        :data="{
          ...data,
          products:
            data.packagingOptions?.map((p) => ({
              ...p,
              count: p.id === data.packaging?.id ? 1 : 0,
            })) || [],
        }"
        :errors="errors"
        :packageType="packageType"
        :selected-items="data.packaging ? [data.packaging] : []"
        :is-package="true"
        :is-single-selection="true"
        :is-packaging="true"
        @update="handleUpdate"
      />
    </div>
  </div>
</template>

<script>
import ProductSelector from '../templates/ProductSelector.vue';
import {
  getPackagingCandidatesFromSelection,
  toPackagingProduct,
  isPackagingModeProduct,
  packagingToProduct,
} from '../utils/packagingHelpers.js';
import { formatPrice } from '../utils/priceUtils.js';
import { decodeHtml } from '../utils/utils.js';

export default {
  components: { ProductSelector },
  props: {
    data: Object,
    errors: Object,
    endpoint: { type: String, required: true },
    packageType: { type: String, default: 'large' },
    mode: { type: String, default: null },
  },
  emits: [
    'update',
    'next',
    'go-to-step',
    'reset-package',
    'start',
    'increase-product',
    'decrease-product',
    'add-to-cart',
    'remove-product',
    'remove-food',
    'remove-packaging',
    'select-base',
    'printing-updated',
    'remove-base-package',
  ],
  computed: {
    packagingCandidates() {
      return getPackagingCandidatesFromSelection(this.data);
    },

    packagingCandidatesWithCurrentPackage() {
      let packages = getPackagingCandidatesFromSelection(this.data);
      if (this.data.packagingProduct) packages.push(this.data.packagingProduct);
      packages?.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      );
      return packages;
    },

    isPackagingProductSelected() {
      return (
        this.data.packagingMode === 'product' && this.data.packagingProduct
      );
    },

    selectedPackagingProduct() {
      return this.data.packagingProduct || null;
    },

    shouldShowPackagingSection() {
      return (
        this.packagingCandidates.length > 0 || this.isPackagingProductSelected
      );
    },
  },

  mounted() {
    this.setupEventListeners();

    fetch('/api/products/boxes')
      .then((res) => res.json())
      .then((data) => {
        if (!data || !Array.isArray(data.items)) {
          return;
        }
        this.$emit('update', { packagingOptions: data.items });

        if (this.data.basePackage?.packaging) {
          const basePackaging = this.data.basePackage.packaging;
          this.$emit('update', {
            packaging: {
              id: basePackaging.id,
              name: basePackaging.name,
              title: basePackaging.title || basePackaging.name,
              price: basePackaging.price || 0,
              image: basePackaging.image,
              imageUrl: basePackaging.image,
              attributes: basePackaging.attributes || [],
            },
          });
        }
      })
      .catch((err) => {});
  },
  beforeUnmount() {
    this.removeEventListeners();
  },
  methods: {
    handleUpdate(payload) {
      const products = Array.isArray(payload.products) ? payload.products : [];

      // Set packaging
      const currentSelectedPackaging = this.data?.packaging;
      const currentSelectedPackagingProduct = this.data?.packagingProduct;
      const newlySelectedPackaging =
        products.find((p) => p.count === 1) || null;
      const newlySelectedPackagingProduct =
        payload?.isPackagingCandidate && payload?.id ? payload : null;

      // Move current product from packaging to products
      if (currentSelectedPackagingProduct !== null) {
        const product = packagingToProduct(currentSelectedPackagingProduct);

        // Switch back to box and add product back to selection
        this.data.products.push(product);
        this.$emit('update', {
          packagingMode: 'box',
          packaging: null,
          packagingProduct: null,
          products: this.data.products,
        });
      }

      // Add new product as packaging
      if (
        newlySelectedPackagingProduct &&
        newlySelectedPackagingProduct?.id !==
          currentSelectedPackagingProduct?.id
      ) {
        const packagingProduct = toPackagingProduct(
          newlySelectedPackagingProduct,
        );
        packagingProduct.count = 1;

        this.$emit('update', {
          packagingMode: 'product',
          packagingProduct: packagingProduct,
          packaging: null, // Clear the original packaging from base package
          removeProductFromSelection: newlySelectedPackagingProduct?.id,
        });
      }

      // Newly selected packaging is a package
      if (newlySelectedPackaging && newlySelectedPackaging?.id) {
        // Remove if current is the same as selected
        if (currentSelectedPackaging?.id === newlySelectedPackaging?.id) {
          this.$emit('update', {
            packaging: null,
            packagingMode: 'box',
            packagingProduct: null,
          });
          return;
        }

        // Set new packaging
        const updatedOptions = products.map((p) => ({
          ...p,
          count:
            newlySelectedPackaging && p.id === newlySelectedPackaging.id
              ? 1
              : 0,
        }));

        this.$emit('update', {
          packaging: newlySelectedPackaging,
          packagingOptions: updatedOptions,
          packagingMode: 'box',
          packagingProduct: null,
        });
      }
    },
    setupEventListeners() {
      this.reloadHandler = () => {
        if (this.$refs.productSelector) {
          this.$refs.productSelector.fetchProducts();
        }
      };
      document.addEventListener('s7_reload_products', this.reloadHandler);
    },
    removeEventListeners() {
      if (this.reloadHandler) {
        document.removeEventListener('s7_reload_products', this.reloadHandler);
      }
    },
    decodedItemName(name) {
      return decodeHtml(name || '');
    },
  },
};
</script>
